import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Sifre en az 6 karakter olmali'); return; }
    if (password !== confirmPassword) { toast.error('Sifreler eslesmiyorr'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Sifre basariyla guncellendi!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sifre sifirlanamadi');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center max-w-md">
          <h2 className="font-playfair text-xl font-bold text-charcoal mb-4">Gecersiz Link</h2>
          <p className="text-charcoal/60 mb-6">Sifre sifirlama linki gecersiz veya eksik.</p>
          <Link to="/login"><Button className="bg-terracotta text-white rounded-full px-8">Giris Sayfasina Don</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/"><img src="/logo.png" alt="Central Restaurant" className="h-16 w-auto mx-auto" /></Link>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-playfair text-2xl font-bold text-charcoal mb-2">Sifre Guncellendi!</h2>
              <p className="text-charcoal/60 mb-6">Sifreniz basariyla guncellendi. Yeni sifrenizle giris yapabilirsiniz.</p>
              <Button onClick={() => navigate('/login')} className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full px-8" data-testid="go-to-login-btn">
                Giris Yap
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-playfair text-2xl font-bold text-charcoal text-center mb-2">Yeni Sifre Belirle</h1>
              <p className="text-charcoal/60 text-center mb-8">Yeni sifrenizi girin</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-charcoal">Yeni Sifre</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="En az 6 karakter"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal/40">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-charcoal">Sifre Tekrar</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Sifrenizi tekrar girin"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-6 text-base font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {loading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Sifremi Guncelle'}
                </Button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
